import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getAppraisal, type AppraisalDetail } from '../lib/appraisal';
import { completeJob, type CompleteJobResult } from '../lib/session';

export function Completion() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [result, setResult] = useState<CompleteJobResult | null>(null);
  const [job, setJob] = useState<AppraisalDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const [r, a] = await Promise.all([
          completeJob(jobId),
          getAppraisal(jobId).catch(() => null),
        ]);
        if (cancelled) return;
        setResult(r);
        setJob(a);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (!jobId) return <div className="p-4 text-sm text-red-600">Missing job ID.</div>;

  if (err) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <h1 className="text-xl font-bold text-red-600">Could not finalize</h1>
        <p className="mt-2 text-sm text-gray-700">{err}</p>
        <div className="mt-4 flex flex-col gap-2">
          <button
            onClick={() => navigate(`/job/${jobId}/sync?${search.toString()}`)}
            className="rounded-lg bg-eva-teal py-3 text-sm font-semibold text-white"
          >
            Retry sync
          </button>
          <Link to={`/job/${jobId}`} className="text-sm text-gray-600 underline">
            Back to job
          </Link>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
        Finalizing…
      </div>
    );
  }

  const completedAt = new Date(result.completed_at);
  const choice = search.get('choice');

  return (
    <div className="mx-auto max-w-md p-6 text-center">
      <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border-4 border-eva-teal">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h1 className="mt-4 text-2xl font-bold">Synced to EVA</h1>
      <p className="mt-1 text-sm text-gray-600">
        {job?.policyholder_name ? `${job.policyholder_name} · ` : ''}
        {result.synced_count} photo{result.synced_count === 1 ? '' : 's'} confirmed
      </p>
      <p className="mt-0.5 text-xs text-gray-500">
        {completedAt.toLocaleString()}
        {choice === 'renumber' && ' · renumbered 1..N'}
        {choice === 'keep_gaps' && ' · gaps preserved'}
      </p>

      <div className="mt-8 flex flex-col gap-2">
        <Link
          to={`/job/${jobId}`}
          className="rounded-lg bg-eva-teal py-3 text-sm font-semibold text-white"
        >
          View in EVA
        </Link>
        <Link to="/" className="text-sm text-gray-600 underline">
          Back to job list
        </Link>
      </div>
    </div>
  );
}
